import React, { useState, useEffect } from 'react';
import { Menu } from 'antd';
import clsx from 'clsx';
import { useAppStateContext } from 'src/stateProviders/useAppStateContext';
import SiderMenuItem, { SiderMenuItemProps } from '../SiderMenuItem';
import qsearchMenuConfig from 'src/configs/qsearchMenuConfig';
import { primaryMenuConfigItem } from 'src/configs/primaryMenuConfigItem';
import optiPriceMenuConfig from 'src/configs/optiPriceMenuConfig';
import productAlertsMenuConfig from 'src/configs/productAlertsMenuConfig';
import interActiveMenuConfig from 'src/configs/interActiveMenuConfig';
import optCenterMenuConfig from 'src/configs/optCenterMenuConfig';
import assortmentMenuConfig from 'src/configs/assortmentMenuConfig';
import styles from './PrimaryMenu.module.less';

const SUB_MENU_ITEM_HORIZONTAL_OFFSET = 26;

const mapConfigToMenuItemProps = (
  config: Array<primaryMenuConfigItem>,
  parentKey: string,
  currentLevel: number = 0
): SiderMenuItemProps[] =>
  config.flatMap(({ children, className, ...rest }, index) => {
    const newKey = `${parentKey}_${index}`;
    return [
      {
        ...rest,
        key: `menu_item_${newKey}`,
        iconStyle: { marginLeft: currentLevel * SUB_MENU_ITEM_HORIZONTAL_OFFSET },
        className: clsx(styles.menuItem, className),
        iconClassName: clsx(styles.menuItemIcon, { [styles.firstLevel]: currentLevel === 0 }),
        labelClassName: styles.menuItemLabel,
      },
      ...(children ? mapConfigToMenuItemProps(children, newKey, currentLevel + 1) : []),
    ];
  });

const getSelectedKeys = (configWithKeys: SiderMenuItemProps[], pathName: string): string[] => {
  const nucleusPathName = '/nucleus';
  const foundItemProps = configWithKeys.find(({ path }) => {
    if (pathName.includes(nucleusPathName)) {
      return path === nucleusPathName;
    }
    return path === pathName;
  });
  return foundItemProps ? [foundItemProps.key] : [];
};

type PrimaryMenuProps = {
  pathName: string;
  className: string;
  hasIqHubPrivilege: boolean;
};

const PrimaryMenu: React.FC<PrimaryMenuProps> = ({ pathName, className, hasIqHubPrivilege }) => {
  const [menuItems, setMenuItems] = useState<SiderMenuItemProps[]>([]);
  const { user } = useAppStateContext();

  const addMenuItems = (newItems: SiderMenuItemProps[]) => {
    setMenuItems((prevItems) => [...prevItems, ...newItems]);
  };

  const filterMenuItems = ({ label }: SiderMenuItemProps) => {
    if (
      (label === 'Price Insights' && !user?.appPermissions['enable_optiprice']) ||
      (label === 'Interactive Reports' && !user?.appPermissions['interactive_reports'])
    ) {
      return false;
    }
    return true;
  };

  useEffect(() => {
    setMenuItems([]);

    // Only load menu items if the user has the 'IQ Hub' privilege
    if (hasIqHubPrivilege) {
      if (user && user.appPermissions) {
        if (user.appPermissions['enable_optiprice']) {
          addMenuItems(mapConfigToMenuItemProps(optiPriceMenuConfig, '0'));
        }
        if (user.appPermissions['enable_product_alert']) {
          addMenuItems(mapConfigToMenuItemProps(productAlertsMenuConfig, '1'));
        }
        if (user.appPermissions['enable_optimix']) {
          addMenuItems(mapConfigToMenuItemProps(assortmentMenuConfig, '2'));
        }
        if (user.appPermissions['enable_datascout']) {
          addMenuItems(mapConfigToMenuItemProps(qsearchMenuConfig, '3'));
        }
        if (user.appPermissions['interactive_reports'] || user.appPermissions['enable_optiprice']) {
          addMenuItems(mapConfigToMenuItemProps(interActiveMenuConfig, '4'));
        }
        if (user.otherPrivilege !== undefined && user.otherPrivilege['Nucleus'] === '10048') {
          addMenuItems(mapConfigToMenuItemProps(optCenterMenuConfig, '5'));
        }
      }
    }
  }, [user]);

  return (
    <Menu
      theme="light"
      mode="inline"
      className={clsx(styles.primaryMenu, className)}
      selectedKeys={getSelectedKeys(menuItems, pathName)}
    >
      {menuItems.filter(filterMenuItems).map(({ key, ...props }) => (
        <SiderMenuItem key={key} {...props} />
      ))}
    </Menu>
  );
};

export default PrimaryMenu;
